<header class="qp-topbar">
  <div class="qp-topbar__inner">
    <button type="button" class="qp-topbar__brand" (click)="exit()" aria-label="Back to the dashboard">
      <img src="assets/logos/freely-logo-black.svg" alt="Freely" />
      <span>accept</span>
    </button>
    <button mat-stroked-button type="button" (click)="exit()">exit</button>
  </div>
</header>

<main class="qp">
  <div class="qp__heading">
    <h1 class="qp__title">Quick Pay</h1>
    <p class="qp__subtitle">
      One-time payment for a walk-in or call-in customer. The customer and invoice don't have to be on file yet.
    </p>
  </div>

  @if (unavailable()) {
    <div class="qp-alert qp-alert--error" role="alert">
      <mat-icon>block</mat-icon>
      <span>{{ unavailable() }}</span>
    </div>
  }

  <form [formGroup]="form" (ngSubmit)="submit()" class="qp__form">
    <div class="qp__grid">
      <!-- ── Who and what ─────────────────────────────────────────── -->
      <section class="qp-card">
        <h2 class="qp-card__title"><mat-icon>receipt_long</mat-icon> Payment details</h2>

        <mat-form-field appearance="outline" class="qp-field" subscriptSizing="fixed">
          <mat-label>Customer number</mat-label>
          <input matInput formControlName="customerNumber" autocomplete="off" data-testid="qp-customer" />
          @if (lookup()?.classification?.customerMatched) {
            <mat-icon matSuffix class="qp-ok">check_circle</mat-icon>
          }
          @if (lookup()?.classification?.customerMatched) {
            <mat-hint class="qp-hint qp-hint--ok">
              {{ lookup()?.customer?.displayName || lookup()?.customer?.companyAccountNumber }}
            </mat-hint>
          } @else if (lookup()?.defaultCustomer) {
            <mat-hint class="qp-hint">
              Default customer: {{ lookup()?.defaultCustomer?.displayName || lookup()?.defaultCustomer?.companyAccountNumber }}
            </mat-hint>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="qp-field" subscriptSizing="fixed">
          <mat-label>Invoice number</mat-label>
          <input matInput formControlName="invoiceNumber" autocomplete="off" data-testid="qp-invoice" />
          @if (lookingUp()) {
            <mat-spinner matSuffix diameter="18" class="qp-spinner"></mat-spinner>
          } @else if (lookup()?.classification?.invoiceMatched && lookup()?.invoice) {
            <mat-icon matSuffix class="qp-ok">check_circle</mat-icon>
          }
          @if (!lookingUp() && lookup()?.classification?.invoiceMatched && lookup()?.invoice) {
            <mat-hint class="qp-hint qp-hint--ok">
              Open balance {{ lookup()!.invoice!.outstandingAmount | currency }}
            </mat-hint>
          }
          @if (form.controls.invoiceNumber.hasError('required') && form.controls.invoiceNumber.touched) {
            <mat-error>Enter the invoice number</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="qp-field" subscriptSizing="fixed">
          <mat-label>Amount</mat-label>
          <span matTextPrefix>$&nbsp;</span>
          <input matInput type="number" min="0.01" step="0.01" formControlName="amount" data-testid="qp-amount" />
          @if (form.controls.amount.invalid && form.controls.amount.touched) {
            <mat-error>Enter an amount greater than zero</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="qp-field" subscriptSizing="fixed">
          <mat-label>Invoice comment (optional)</mat-label>
          <textarea matInput rows="3" formControlName="comment" maxlength="500" data-testid="qp-comment"></textarea>
          <mat-hint align="end">{{ form.controls.comment.value.length }} / 500</mat-hint>
        </mat-form-field>

        @if (notice(); as message) {
          <div class="qp-alert qp-alert--warn" data-testid="qp-notice">
            <mat-icon>info</mat-icon>
            <span>{{ message }}</span>
          </div>
        }
      </section>

      <!-- ── How ──────────────────────────────────────────────────── -->
      <section class="qp-card qp-card--payment">
        <h2 class="qp-card__title"><mat-icon>credit_card</mat-icon> Payment method</h2>
        <app-one-time-payment
          [hideCustomerInfo]="true"
          [forceNoSave]="true"
          (formValidated)="paymentValid.set($event)" />
        @switch (costOfCapital.reason) {
          @case ('credit') {
            <p class="qp-coc qp-coc--on" data-testid="qp-coc-note">
              <mat-icon>add_card</mat-icon>
              Credit card: a {{ costOfCapital.percent }}% cost of capital ({{ costOfCapital.amount | currency }}) is added.
            </p>
          }
          @case ('unverified') {
            <p class="qp-coc" data-testid="qp-coc-note">
              <mat-icon>info</mat-icon>
              A {{ costOfCapital.percent }}% cost of capital is added if this is a credit card.
            </p>
          }
          @case ('debit') {
            <p class="qp-coc" data-testid="qp-coc-note">
              <mat-icon>check</mat-icon> No cost of capital on a debit or prepaid card.
            </p>
          }
          @case ('blocked') {
            <p class="qp-coc" data-testid="qp-coc-note">
              <mat-icon>block</mat-icon> No cost of capital for cards billed in {{ cocBlockedState() }}.
            </p>
          }
        }
      </section>
    </div>

    <div class="qp-footer">
      <div class="qp-footer__total">
        @if (costOfCapital.applies) {
          <span class="qp-footer__line">
            {{ (form.controls.amount.value || 0) | currency }} + {{ costOfCapital.amount | currency }} cost of capital
          </span>
        }
        <span>Total</span>
        <strong data-testid="qp-total">{{ costOfCapital.total | currency }}</strong>
      </div>
      <div class="qp-footer__actions">
        <button mat-stroked-button type="button" (click)="exit()">cancel</button>
        <button mat-raised-button color="primary" type="submit" [disabled]="!canSubmit" data-testid="qp-submit">
          @if (submitting()) {
            <mat-spinner diameter="18" class="qp-spinner"></mat-spinner>
          } @else {
            charge {{ costOfCapital.total | currency }}
          }
        </button>
      </div>
    </div>
  </form>
</main>
